import { readFileSync, existsSync, readdirSync } from "node:fs";
import { join } from "node:path";
import { slugify } from "./content-card-images.js";
import { grammarAreaPosKey, grammarOverviewTopicLabel } from "./grammar-topic-labels.js";
import { grammarTopicLocaleLabel } from "./grammar-topics-locale.js";
import { getDataJsonRoot } from "./paths.js";
import grammarCommonCatalog from "../../data/json/locales/grammar-common.json" with { type: "json" };
import grammarUnitUiCatalog from "../../data/json/locales/grammar-units-ui.json" with { type: "json" };
import { mergeLocalePack } from "./locale-resolve.js";
import { UI_LANGS } from "./url-i18n.js";

/** @type {Map<string, { ui: Record<string, unknown>, target: Record<string, unknown> } | null>} */
const grammarUnitPackCache = new Map();

function grammarUnitPackCacheEnabled() {
  if (process.env.GRAMMAR_UNIT_LOCALE_CACHE === "0") return false;
  if (process.env.LL_DEV_FAST === "1") return false;
  return process.env.NODE_ENV === "production";
}

/** Drop cached per-unit locale overlays (tests / dev reload). */
export function clearGrammarUnitPackCache(unitId = "", uiLang = "") {
  const id = String(unitId || "").trim();
  const lang = String(uiLang || "").trim().toLowerCase();
  if (!id && !lang) {
    grammarUnitPackCache.clear();
    return;
  }
  for (const key of grammarUnitPackCache.keys()) {
    if (id && lang) {
      if (key === `${id}:${normalizeGrammarUiLang(lang)}`) grammarUnitPackCache.delete(key);
    } else if (id && key.startsWith(`${id}:`)) {
      grammarUnitPackCache.delete(key);
    } else if (lang && key.endsWith(`:${normalizeGrammarUiLang(lang)}`)) {
      grammarUnitPackCache.delete(key);
    }
  }
}

/** @type {Map<string, object> | null} */
let bySlug = null;
/** @type {Map<string, object> | null} */
let byId = null;

function grammarDir() {
  const root = getDataJsonRoot();
  if (!root) return null;
  const p = join(root, "de", "grammar");
  return existsSync(p) ? p : null;
}

function loadIndex() {
  if (bySlug) return;
  bySlug = new Map();
  byId = new Map();
  const dir = grammarDir();
  if (!dir) return;
  for (const f of readdirSync(dir)) {
    if (!f.endsWith(".json") || f === "index.json") continue;
    const doc = JSON.parse(readFileSync(join(dir, f), "utf8"));
    if (doc?.slug) {
      const raw = String(doc.slug).trim();
      bySlug.set(raw, doc);
      const canonical = slugify(raw);
      if (canonical) bySlug.set(canonical, doc);
    }
    if (doc?.id) byId.set(String(doc.id), doc);
  }
}

/** Canonical URL slug for a grammar unit document or slug string. */
export function canonicalGrammarUnitSlug(docOrSlug) {
  const raw =
    typeof docOrSlug === "string"
      ? String(docOrSlug || "").trim()
      : String(docOrSlug?.slug || "").trim();
  return raw ? slugify(raw) : "";
}

export function getGrammarUnitBySlug(slug) {
  loadIndex();
  const raw = String(slug || "").trim();
  if (!raw) return null;
  return bySlug?.get(raw) || bySlug?.get(slugify(raw)) || null;
}

export function getGrammarUnitById(id) {
  loadIndex();
  return byId?.get(String(id || "").trim()) || null;
}

/** @type {Map<string, string> | null} */
let localizedSlugToUnitId = null;

function loadLocalizedSlugIndex() {
  if (localizedSlugToUnitId) return;
  localizedSlugToUnitId = new Map();
  loadIndex();
  for (const doc of byId?.values() || []) {
    const slug = canonicalGrammarUnitSlug(doc);
    const id = String(doc?.id || "").trim();
    if (slug && id) localizedSlugToUnitId.set(`de:${slug}`, id);
  }
  const catalog =
    grammarUnitUiCatalog && typeof grammarUnitUiCatalog === "object"
      ? /** @type {Record<string, Record<string, Record<string, unknown>>>} */ (grammarUnitUiCatalog)
      : {};
  for (const [uiLang, units] of Object.entries(catalog)) {
    if (!units || typeof units !== "object") continue;
    for (const [unitId, ui] of Object.entries(units)) {
      if (!String(unitId).startsWith("gra") || !ui || typeof ui !== "object") continue;
      const slug = slugify(String(ui?.slug || "").trim());
      if (slug) localizedSlugToUnitId.set(`${uiLang}:${slug}`, unitId);
    }
  }
}

/** Resolve a grammar unit from a URL slug (German canonical or localized UI slug). */
export function resolveGrammarUnitByRequestSlug(requestSlug, uiLang = "de") {
  const raw = String(requestSlug || "").trim();
  if (!raw) return null;
  const direct = getGrammarUnitBySlug(raw);
  if (direct) return direct;
  loadLocalizedSlugIndex();
  const folded = slugify(raw);
  const lang = normalizeGrammarUiLang(uiLang);
  const unitId = localizedSlugToUnitId?.get(`${lang}:${folded}`);
  if (unitId) return getGrammarUnitById(unitId);
  for (const [key, id] of localizedSlugToUnitId || []) {
    if (key.endsWith(`:${folded}`)) return getGrammarUnitById(id);
  }
  return null;
}

/** Catalog id from a full unit doc, API index row, or overview entry. */
export function grammarUnitCatalogId(unit, overviewUnit) {
  return String(unit?.id || unit?.unitId || overviewUnit?.id || "").trim();
}

/** Canonical URL slug for a unit in the active UI language. */
export function getGrammarUnitUrlSlug(unit, overviewUnit, uiLang = "de") {
  const catalogId = grammarUnitCatalogId(unit, overviewUnit);
  const loc = resolveUnitUiLocale(
    catalogId && !(unit?.id || unit?.unitId) ? { id: catalogId } : unit,
    uiLang,
  );
  const fromPack = slugify(String(loc?.slug || "").trim());
  if (fromPack) return fromPack;
  const full = catalogId ? getGrammarUnitById(catalogId) : null;
  if (full && full !== unit) {
    const fromDoc = slugify(String(resolveUnitUiLocale(full, uiLang)?.slug || "").trim());
    if (fromDoc) return fromDoc;
  }
  return (
    canonicalGrammarUnitSlug(full?.slug || unit?.slug || overviewUnit?.slug) ||
    canonicalGrammarUnitSlug(unit) ||
    ""
  );
}

export function getGrammarUnitLocaleStrings(unitId) {
  const doc = getGrammarUnitById(unitId);
  return doc?.locale && typeof doc.locale === "object" ? doc.locale : {};
}

function normalizeGrammarUiLang(uiLang) {
  const code = String(uiLang || "de")
    .trim()
    .toLowerCase()
    .split(/[-_]/)[0];
  if (UI_LANGS.includes(code)) return code;
  return "de";
}

/** @param {string} unitId @param {string} uiLang */
export function loadGrammarUnitLocalePack(unitId, uiLang) {
  const id = String(unitId || "").trim();
  if (!id) return null;
  const lang = normalizeGrammarUiLang(uiLang);
  const cacheKey = `${id}:${lang}`;
  const cacheEnabled = grammarUnitPackCacheEnabled();
  if (cacheEnabled && grammarUnitPackCache.has(cacheKey)) {
    return grammarUnitPackCache.get(cacheKey) ?? null;
  }
  const byLang =
    grammarUnitUiCatalog && typeof grammarUnitUiCatalog === "object"
      ? /** @type {Record<string, Record<string, Record<string, unknown>>>} */ (grammarUnitUiCatalog)
      : {};
  const ui = byLang[lang]?.[id];
  if (!ui || typeof ui !== "object") {
    if (cacheEnabled) grammarUnitPackCache.set(cacheKey, null);
    return null;
  }
  const pack = { ui, target: {} };
  if (cacheEnabled) grammarUnitPackCache.set(cacheKey, pack);
  return pack;
}

function embeddedGrammarUnitLocale(unit, unitId) {
  if (unit?.locale && typeof unit.locale === "object") return unit.locale;
  return getGrammarUnitLocaleStrings(unitId) || {};
}

/** Resolved per-unit locale bundle (UI language + German target strings). */
export function resolveUnitUiLocale(unit, uiLang = "de") {
  const unitId = grammarUnitCatalogId(unit);
  const embedded = embeddedGrammarUnitLocale(unit, unitId);
  const pack = loadGrammarUnitLocalePack(unitId, uiLang);
  if (pack) {
    return mergeLocalePack(pack.ui, embedded);
  }
  if (normalizeGrammarUiLang(uiLang) === "de") {
    return embedded;
  }
  return embedded;
}

export function getGrammarUnitCommonStrings(uiLang = "de") {
  const code = normalizeGrammarUiLang(uiLang);
  const catalog =
    grammarCommonCatalog && typeof grammarCommonCatalog === "object"
      ? /** @type {Record<string, Record<string, unknown>>} */ (grammarCommonCatalog)
      : {};
  const de = catalog.de && typeof catalog.de === "object" ? catalog.de : {};
  if (code === "de") return de;
  const pack = catalog[code] && typeof catalog[code] === "object" ? catalog[code] : null;
  if (!pack) return de;
  return mergeLocalePack(pack, de);
}

/** Clear cached grammar common strings (kept for tests / public API compatibility). */
export function clearGrammarUnitsCommonCache() {
  // Common strings are loaded from a generated static snapshot.
}

function resolveGrammarUnitCommon(unit, uiLang = "de") {
  const shared = getGrammarUnitCommonStrings(uiLang);
  if (normalizeGrammarUiLang(uiLang) === "de" && unit?.localeCommon && typeof unit.localeCommon === "object") {
    return mergeLocalePack(unit.localeCommon, shared);
  }
  return shared;
}

/**
 * Localized title, description, teaser, and topic labels for list/detail chrome.
 * @param {Record<string, unknown> | null | undefined} unit
 * @param {Record<string, unknown> | null | undefined} overviewUnit
 * @param {string} [uiLang]
 */
export function getGrammarUnitPresentation(unit, overviewUnit, uiLang = "de") {
  const loc = resolveUnitUiLocale(unit, uiLang);
  const title =
    String(loc?.title || "").trim() ||
    String(unit?.title || "").trim() ||
    String(overviewUnit?.title || "").trim();
  const description =
    String(loc?.description || "").trim() ||
    String(overviewUnit?.description || "").trim() ||
    String(unit?.description || "").trim();
  const teaser =
    String(loc?.teaser || "").trim() ||
    String(overviewUnit?.teaser || "").trim() ||
    String(unit?.teaser || "").trim();
  const topicLabels =
    loc?.topics && typeof loc.topics === "object" ? /** @type {Record<string, string>} */ (loc.topics) : null;
  const rawTopics = Array.isArray(overviewUnit?.topics)
    ? overviewUnit.topics
    : Array.isArray(unit?.topics)
      ? unit.topics
      : [];
  const topics = rawTopics.map((topic) => {
    const id = String(topic?.id || "").trim();
    const fromUnit = topicLabels && id ? String(topicLabels[id] || "").trim() : "";
    const fromCatalog = !fromUnit && id ? grammarTopicLocaleLabel(id, uiLang).trim() : "";
    const localized = fromUnit || fromCatalog;
    return {
      ...topic,
      name: localized || String(topic?.nameDe || topic?.name || id).trim(),
    };
  });
  return { title, description, teaser, topics };
}

/**
 * Topic chips for the Grammatik hub: plural POS family labels (Wortschatz catalog), not locale pack sentences.
 *
 * @param {Record<string, unknown> | null | undefined} unit
 * @param {string} [uiLang]
 */
export function getGrammarUnitOverviewTopics(unit, uiLang = "de") {
  const rawTopics = Array.isArray(unit?.topics)
    ? unit.topics
    : Array.isArray(unit?.topicIds)
      ? unit.topicIds.map((id) => ({ id: String(id || "").trim() }))
      : [];
  const seen = new Set();
  return rawTopics
    .map((topic) => {
      if (!topic || typeof topic !== "object") return null;
      const id = String(topic?.id || "").trim();
      if (!id) return null;
      const name = grammarOverviewTopicLabel(uiLang, topic);
      return {
        ...topic,
        id,
        name,
        nameDe: String(topic?.nameDe || "").trim() || name,
      };
    })
    .filter((row) => {
      if (!row) return false;
      const dedupeKey = grammarAreaPosKey(String(row.area || "").trim()) || row.name;
      if (seen.has(dedupeKey)) return false;
      seen.add(dedupeKey);
      return true;
    });
}

/**
 * Resolve a localized key. Keys starting with ``common.`` are looked up in
 * the shared common bundle; everything else is resolved against the
 * per-unit ``loc`` bundle. Returns "" when the key cannot be resolved.
 */
function pick(loc, dotted, common) {
  const commonBundle = common || grammarUnitsCommonDe;
  const path = String(dotted || "");
  if (!path) return "";
  const parts = path.split(".").filter(Boolean);
  if (!parts.length) return "";
  let cur;
  if (parts[0] === "common") {
    cur = commonBundle;
    parts.shift();
  } else {
    cur = loc;
  }
  for (const p of parts) {
    if (cur == null || typeof cur !== "object") return "";
    cur = cur[p];
  }
  return typeof cur === "string" ? cur : "";
}

function _walk(loc, dotted, common) {
  const commonBundle = common || grammarUnitsCommonDe;
  const parts = String(dotted || "").split(".").filter(Boolean);
  if (!parts.length) return null;
  let cur;
  if (parts[0] === "common") {
    cur = commonBundle;
    parts.shift();
  } else {
    cur = loc;
  }
  for (const p of parts) {
    if (cur == null || typeof cur !== "object") return null;
    cur = cur[p];
  }
  return cur;
}

function pickArray(obj, dotted, common) {
  const cur = _walk(obj, dotted, common);
  return Array.isArray(cur) ? cur : [];
}

/** @param {unknown} raw */
export function normalizeExplanationParagraphs(raw) {
  if (Array.isArray(raw)) {
    return raw.map((p) => String(p || "").trim()).filter(Boolean);
  }
  if (raw && typeof raw === "object" && Array.isArray(raw.paragraphs)) {
    return raw.paragraphs.map((p) => String(p || "").trim()).filter(Boolean);
  }
  const text = String(raw || "").trim();
  if (!text) return [];
  return text
    .split(/\n\n+/)
    .map((p) => p.trim())
    .filter(Boolean);
}

function pickObject(obj, dotted, common) {
  const cur = _walk(obj, dotted, common);
  return cur && typeof cur === "object" ? cur : null;
}

function resolveExplanation(loc, u, common) {
  if (u.explanationKey) {
    return normalizeExplanationParagraphs(_walk(loc, u.explanationKey, common));
  }
  return normalizeExplanationParagraphs(u.explanation);
}

export function resolveGrammarUnitModules(unit, uiLang = "de") {
  // UI overlays come from the generated content-locale snapshot; German
  // target-language text stays embedded in the generated unit document.
  const loc = resolveUnitUiLocale(unit, uiLang);
  const common = resolveGrammarUnitCommon(unit, uiLang);
  const raw = unit.modules?.grammar?.units || [];
  const units = raw.map((u) => {
    if (!u || typeof u !== "object") return null;
    const title = u.titleKey ? pick(loc, u.titleKey, common) : u.title || "";
    const instructions = u.instructionKey
      ? pick(loc, u.instructionKey, common)
      : u.instructionsKey
        ? pick(loc, u.instructionsKey, common)
        : u.instructions || "";
    if (u.type === "rule") {
      return {
        ...u,
        type: "rule",
        title,
        explanationParagraphs: resolveExplanation(loc, u, common),
        examples: u.examplesKey ? pickObject(loc, u.examplesKey, common) : u.examples,
      };
    }
    if (u.type === "anchors") {
      return {
        type: "anchors",
        title: title || pick(loc, "common.section.examples", common),
        items: Array.isArray(u.items) ? u.items : [],
      };
    }
    if (u.type === "closure") {
      // Closure / "Merksatz" sections were removed from the design — drop
      // them silently so legacy generated units don't show an empty block.
      return null;
    }
    const items = (u.items || []).map((item) => {
      const resolved = {
        ...item,
        instructions: item.instructionKey ? pick(loc, item.instructionKey, common) : item.instructions || instructions,
        title: item.titleKey ? pick(loc, item.titleKey, common) : item.title || "",
        prompt: item.promptKey ? pick(loc, item.promptKey, common) : item.prompt || "",
        elements: item.elementsKey ? pickArray(loc, item.elementsKey, common) : item.elements || [],
        hint: item.hintKey ? pick(loc, item.hintKey, common) : item.hint || "",
      };
      // Lückentext / multi-blank: a localized text body via textKey is
      // expanded into the ``text`` lines used by the renderer.
      if (item.textKey) {
        const text = _walk(loc, item.textKey, common);
        if (Array.isArray(text)) {
          const lines = text.map((entry) => String(entry || "").trim()).filter(Boolean);
          if (lines.length) resolved.text = lines;
        } else {
          const line = String(text || "").trim();
          if (line) resolved.text = [line];
        }
      }
      return resolved;
    });
    return { ...u, type: "exercise", title, instructions, items };
  }).filter(Boolean);
  return { grammar: { units } };
}

function distractorWords(sentences, correctWords, limit = 4) {
  const correct = new Set(correctWords);
  const out = [];
  for (const sent of sentences || []) {
    for (const tok of sent.tokens || []) {
      const s = String(tok.s || "").trim();
      if (s.length < 3 || correct.has(s) || out.includes(s)) continue;
      out.push(s);
      if (out.length >= limit) return out;
    }
  }
  return out;
}

function getMorphologicalDistractors(s, l, g, m) {
  const s_lower = String(s || "").trim().toLowerCase();
  const l_lower = String(l || "").trim().toLowerCase();
  const g_str = String(g || "").trim();
  const m_str = String(m || "").trim();

  if (l_lower === "sein") {
    const candidates = ["bin", "bist", "ist", "sind", "seid", "war", "waren"];
    return candidates.filter(c => c.toLowerCase() !== s_lower).slice(0, 3);
  }
  if (l_lower === "haben") {
    const candidates = ["habe", "hast", "hat", "haben", "habt", "hatte", "hatten"];
    return candidates.filter(c => c.toLowerCase() !== s_lower).slice(0, 3);
  }
  if (l_lower === "können") {
    return ["kann", "kannst", "können", "könnt", "konnte"].filter(c => c.toLowerCase() !== s_lower).slice(0, 3);
  }
  if (l_lower === "müssen") {
    return ["muss", "musst", "müssen", "müsst", "musste"].filter(c => c.toLowerCase() !== s_lower).slice(0, 3);
  }
  if (l_lower === "wollen") {
    return ["will", "willst", "wollen", "wollt", "wollte"].filter(c => c.toLowerCase() !== s_lower).slice(0, 3);
  }
  if (l_lower === "sollen") {
    return ["soll", "sollst", "sollen", "sollt", "sollte"].filter(c => c.toLowerCase() !== s_lower).slice(0, 3);
  }
  if (l_lower === "dürfen") {
    return ["darf", "darfst", "dürfen", "dürft", "durfte"].filter(c => c.toLowerCase() !== s_lower).slice(0, 3);
  }
  if (l_lower === "mögen") {
    return ["mag", "magst", "mögen", "mögt", "möchte"].filter(c => c.toLowerCase() !== s_lower).slice(0, 3);
  }

  if (g_str.startsWith("DET") || g_str.startsWith("ART")) {
    if (["der", "die", "das", "den", "dem", "des"].includes(s_lower)) {
      const candidates = ["der", "die", "das", "den", "dem"];
      const isCap = s[0] === s[0].toUpperCase();
      return candidates
        .filter(c => c.toLowerCase() !== s_lower)
        .map(c => isCap ? c.charAt(0).toUpperCase() + c.slice(1) : c)
        .slice(0, 3);
    }
    let base = "";
    const prefixes = ["ein", "kein", "mein", "dein", "sein", "ihr", "unser", "euer", "Ihr"];
    for (const prefix of prefixes) {
      if (s_lower.startsWith(prefix.toLowerCase())) {
        base = s[0] === s[0].toUpperCase() ? prefix.charAt(0).toUpperCase() + prefix.slice(1) : prefix.toLowerCase();
        break;
      }
    }
    if (base) {
      const endings = ["", "e", "en", "em", "er", "es"];
      const candidates = endings
        .map(e => {
          if (e === "es" && base.toLowerCase() === "euer") return null;
          return base + e;
        })
        .filter(Boolean);
      return candidates.filter(c => c !== s).slice(0, 3);
    }
  }

  if (g_str === "ADJ") {
    let base = s;
    const endings = ["en", "er", "es", "em", "e"];
    for (const e of endings) {
      if (s.endsWith(e) && s.length > e.length + 2) {
        base = s.slice(0, -e.length);
        break;
      }
    }
    if (base !== s) {
      const adjEndings = ["e", "en", "er", "es", "em"];
      return adjEndings.map(e => base + e).filter(c => c !== s).slice(0, 3);
    }
  }

  if (g_str.startsWith("V")) {
    let base = s;
    const endings = ["st", "en", "te", "ten", "t", "e"];
    for (const e of endings) {
      if (s.endsWith(e) && s.length > e.length + 2) {
        base = s.slice(0, -e.length);
        break;
      }
    }
    if (base !== s) {
      const verbEndings = ["e", "st", "t", "en", "te"];
      return verbEndings.map(e => base + e).filter(c => c !== s).slice(0, 3);
    }
  }

  return [s + "en", s + "t", s + "e"].slice(0, 3);
}

/**
 * When false, grammar exercises on linked stories/dialogs keep authored example
 * sentences only (no substitution from the reader text).
 */
const GRAMMAR_USE_STORY_SENTENCES = false;

/** Whether grammar units hydrate exercises from the linked story/dialog sentence pool. */
export function grammarUsesStorySentencePool() {
  return GRAMMAR_USE_STORY_SENTENCES;
}

/* ============================================================
 * Story-pattern substitution
 *
 * Recipes annotate "Im Text finden" anchors and select-blank items with
 * ``storyPattern`` filters. When a story sentence matches the filter we
 * swap the authored content for the real story sentence; otherwise the
 * authored fallback is kept verbatim.
 * ============================================================ */

function morphValue(tok) {
  return String(tok?.m || "").trim();
}

/** Match a morph tag piece against dotted tags like ``ind.pres.3.sg.fin``. */
function morphContainsPiece(morph, piece) {
  const m = String(morph || "").trim();
  const p = String(piece || "").trim();
  if (!p) return true;
  if (!m) return false;
  const segments = m.split(".").filter(Boolean);
  if (p.includes(".")) {
    const need = p.split(".").filter(Boolean);
    let j = 0;
    for (let i = 0; i < segments.length && j < need.length; i++) {
      if (segments[i] === need[j]) j += 1;
    }
    return j === need.length;
  }
  return segments.includes(p);
}

function tokenMatchesPattern(tok, pattern) {
  if (!tok || !pattern || typeof pattern !== "object") return false;
  const lemmaLower = String(tok.l || "").trim().toLowerCase();
  if (typeof pattern.lemma === "string") {
    if (lemmaLower !== pattern.lemma.toLowerCase()) return false;
  } else if (Array.isArray(pattern.lemma)) {
    const set = new Set(pattern.lemma.map((x) => String(x).toLowerCase()));
    if (!set.has(lemmaLower)) return false;
  }
  if (Array.isArray(pattern.lemma_in)) {
    const set = new Set(pattern.lemma_in.map((x) => String(x).toLowerCase()));
    if (!set.has(lemmaLower)) return false;
  }
  const m = morphValue(tok);
  if (Array.isArray(pattern.morph_all_of)) {
    for (const piece of pattern.morph_all_of) {
      if (!morphContainsPiece(m, piece)) return false;
    }
  }
  if (Array.isArray(pattern.morph_any_of)) {
    let ok = false;
    for (const piece of pattern.morph_any_of) {
      if (morphContainsPiece(m, piece)) { ok = true; break; }
    }
    if (!ok) return false;
  }
  if (typeof pattern.g_prefix === "string") {
    const g = String(tok.g || "");
    if (!g.startsWith(pattern.g_prefix)) return false;
  }
  if (typeof pattern.g === "string") {
    if (String(tok.g || "") !== pattern.g) return false;
  }
  return true;
}

function sentenceLengthOk(sent, pattern) {
  if (!pattern) return true;
  const tokens = sent?.tokens || [];
  const contentTokens = tokens.filter((t) => String(t?.s || "").trim().length > 0);
  const n = contentTokens.length;
  if (typeof pattern.max_tokens === "number" && n > pattern.max_tokens) return false;
  if (typeof pattern.min_tokens === "number" && n < pattern.min_tokens) return false;
  return true;
}

/**
 * Sentence-level pattern match. Used for **recognition pools** and
 * other situations where the *whole sentence* (not a single target
 * token) is the unit being matched. Supports:
 *
 * - All token-level filters (`lemma`, `morph_all_of`, `morph_any_of`,
 *   `g_prefix`, `g`): satisfied if *any* token in the sentence
 *   matches (so the sentence "contains" a token of that shape).
 * - `forbid_morph_any_of`: sentence is rejected if *any* token has one
 *   of these morph segments (useful for "Kein Imperativ" style pools
 *   that need a verb sentence with no imperative anywhere).
 * - `max_tokens` / `min_tokens`: length caps on content tokens.
 */
function sentenceMatchesPattern(sent, pattern) {
  if (!sent || !pattern || typeof pattern !== "object") return false;
  if (!sentenceLengthOk(sent, pattern)) return false;
  const tokens = sent.tokens || [];
  if (Array.isArray(pattern.forbid_morph_any_of) && pattern.forbid_morph_any_of.length) {
    for (const tok of tokens) {
      const m = morphValue(tok);
      for (const piece of pattern.forbid_morph_any_of) {
        if (morphContainsPiece(m, piece)) return false;
      }
    }
  }
  // Only require a positive per-token hit when the pattern actually
  // declares one (lemma / morph / g filters). A pattern that only
  // declares ``forbid_*`` filters is satisfied by any sentence passing
  // the negative checks.
  const hasPositiveFilter =
    pattern.lemma !== undefined ||
    pattern.lemma_in !== undefined ||
    (Array.isArray(pattern.morph_all_of) && pattern.morph_all_of.length) ||
    (Array.isArray(pattern.morph_any_of) && pattern.morph_any_of.length) ||
    pattern.g_prefix !== undefined ||
    pattern.g !== undefined;
  if (!hasPositiveFilter) return true;
  for (const tok of tokens) {
    if (tokenMatchesPattern(tok, pattern)) return true;
  }
  return false;
}

function storyMatchContentTokenCount(sent) {
  return (sent?.tokens || []).filter((t) => String(t?.s || "").trim().length > 0).length;
}

function findStoryMatch(sentences, pattern, usedRefs, options) {
  if (!Array.isArray(sentences) || !pattern) return null;
  const opts = options && typeof options === "object" ? options : {};
  const sharedRefs = opts.sharedRefs instanceof Set ? opts.sharedRefs : null;
  const candidates = [];
  for (const sent of sentences) {
    if (usedRefs && sent?.ref && usedRefs.has(sent.ref)) continue;
    if (!sentenceLengthOk(sent, pattern)) continue;
    const tokens = sent?.tokens || [];
    for (const tok of tokens) {
      if (tokenMatchesPattern(tok, pattern)) {
        const reused = sharedRefs && sent.ref ? sharedRefs.has(sent.ref) : false;
        candidates.push({ sentence: sent, token: tok, reused });
        break;
      }
    }
  }
  if (!candidates.length) return null;
  // Cross-block diversity: candidates not seen in earlier blocks come
  // first; reused refs are kept as a last resort.
  const ranked = candidates.map(function (c) {
    return {
      sentence: c.sentence,
      token: c.token,
      reused: c.reused,
      hasBracket: verbBracketEndIndex(c.sentence.tokens) != null,
      tokenCount: storyMatchContentTokenCount(c.sentence),
    };
  });
  ranked.sort(function (a, b) {
    if (a.reused !== b.reused) return a.reused ? 1 : -1;
    if (opts.preferVerbBracket && a.hasBracket !== b.hasBracket) return a.hasBracket ? -1 : 1;
    return a.tokenCount - b.tokenCount;
  });
  return ranked[0];
}

function rebuildBlankText(text, tokens, targetIndexes) {
  // Token ``i`` fields are raw-list indexes (with whitespace tokens stripped),
  // so we cannot use them as character offsets. Instead we walk ``text``
  // matching each compact token by surface form and re-emit either the
  // surface or a ``*`` blank, while preserving inter-token whitespace.
  if (!Array.isArray(tokens) || tokens.length === 0 || !text) return text || "";
  const set = new Set((targetIndexes || []).map((x) => Number(x)));
  let cursor = 0;
  let out = "";
  for (const tok of tokens) {
    const surface = String(tok.s || "");
    if (!surface) continue;
    const idx = text.indexOf(surface, cursor);
    if (idx < 0) continue;
    if (idx > cursor) out += text.slice(cursor, idx);
    out += set.has(Number(tok.i)) ? "*" : surface;
    cursor = idx + surface.length;
  }
  if (cursor < text.length) out += text.slice(cursor);
  return stripTrailingClosingQuotes(out);
}

/** Strip stray closing quotation marks left on story-sourced sentences. */
function stripTrailingClosingQuotes(text) {
  return String(text || "").replace(/[\s]*(?:["""\u201e\u201c\u201d»]+[\s]*)+$/, "").trimEnd();
}

/** Remove an orphan trailing comma from a standalone sentence fragment. */
function stripTrailingComma(text) {
  return String(text || "").replace(/,\s*$/, "");
}

function stripTrailingCommaTokens(tokens) {
  const out = (tokens || []).slice();
  while (out.length) {
    if (String(out[out.length - 1]?.s || "").trim() === ",") out.pop();
    else break;
  }
  return out;
}

/**
 * Balance imbalanced quote marks introduced by sentence segmentation.
 *
 * Sentence-tokenizers regularly split direct speech across multiple
 * sentences, leaving one half of a quotation pair stranded — e.g.
 * *„Komm bitte schnell!* (no closing) or *Anna, geh!"* (no opening).
 * The grammar exercises display the full sentence with all
 * punctuation, so an orphan quote looks confusingly wrong.
 *
 * Strategy (intentionally conservative): only act on the canonical
 * German pairs (`„` / `"`, `«` / `»`). If a sentence has at least one
 * closer but no opener, prepend `„`; if it has at least one opener but
 * no closer, append `"`. We don't try to repair more exotic patterns —
 * those stay as-is so authors notice them.
 */
function balanceQuotes(text) {
  let t = String(text || "");
  if (!t) return t;
  const opens = (t.match(/[\u201E\u00AB]/g) || []).length;
  const closes = (t.match(/[\u201C\u201D\u00BB]/g) || []).length;
  if (opens === 0 && closes >= 1) t = "\u201E" + t;
  else if (closes === 0 && opens >= 1) t = t + "\u201C";
  return t;
}

/**
 * Insert the comma required before a dialogue attribution when the
 * source tokenizer dropped it. German rule: *„…!", schlägt Max vor.*
 * not *„…!" schlägt Max vor.*
 */
function ensureDialogueAttributionComma(text) {
  return String(text || "").replace(
    /([\u201C\u201D\u00BB"'])(\s+)(?![,;:])([a-zäöüß])/gu,
    "$1,$2$3"
  );
}

/** Normalise story sentence surface text before punctuation enrichment. */
function normalizeStorySentenceText(text) {
  return stripTrailingComma(ensureDialogueAttributionComma(String(text || "")));
}

/** Present a story fragment with balanced German quotation marks. */
export function balanceDisplaySentenceText(text) {
  return balanceQuotes(normalizeStorySentenceText(String(text || "")));
}

function balanceSelectedV3ExerciseItem(item) {
  if (!item || typeof item !== "object") return item;
  if (item.type === "mark_in_text" && Array.isArray(item.markInTextSentences)) {
    const markInTextSentences = item.markInTextSentences.map((sent) => {
      const normalized = balanceSentenceQuotes({
        ...sent,
        text: normalizeStorySentenceText(sent?.text || ""),
      });
      return addModalBracketTargetIfNeeded(normalized);
    });
    return {
      ...item,
      text: markInTextSentences.map((s) => s.text).filter(Boolean),
      markInTextSentences,
    };
  }
  if (item.type === "select_one" && Array.isArray(item.text)) {
    return {
      ...item,
      text: item.text.map((line) => balanceDisplaySentenceText(line)),
    };
  }
  return item;
}

/**
 * Story tokens come from the tokenizer with all punctuation stripped
 * (commas, full-stops, exclamation marks, quotes …). The renderer
 * walks ``tokens`` to lay out the sentence, so anything missing from
 * ``tokens`` is invisible — which makes story-derived `Wörter finden`
 * sentences read like *"Wähle eins aus das dir gefällt"* instead of
 * *"Wähle eins aus, das dir gefällt."*.
 *
 * This helper walks the original ``text`` and the existing word
 * tokens, then emits **synthetic** punctuation tokens for every char
 * that appears in the gaps. Synthetic tokens get distinct negative
 * (or large) ``i`` values so they can't collide with real token
 * indexes used by ``targetIndexes`` / selection state.
 */
function enrichTokensWithPunctuation(text, tokens) {
  const src = normalizeStorySentenceText(text);
  if (!src || !Array.isArray(tokens) || !tokens.length) return tokens || [];
  // Don't double-enrich: if the existing tokens already include any
  // punctuation surface, assume the tokenizer kept them and leave the
  // array alone.
  for (const tok of tokens) {
    const s = String(tok?.s || "").trim();
    if (s && /^[.,;:!?\u2014\u2013\u2026"\u201E\u201C\u201D\u00AB\u00BB'\u2018\u2019\u201A]+$/.test(s)) {
      return tokens;
    }
  }
  // Punctuation we recognise; non-matching characters (digits, letters)
  // in the gap are ignored so a malformed tokenizer can't leak through.
  const PUNCT_CHAR = /[.,;:!?\u2014\u2013\u2026"\u201E\u201C\u201D\u00AB\u00BB'\u2018\u2019\u201A]/;
  const out = [];
  let cursor = 0;
  let synthIdx = -1000;
  const emitGap = (start, end) => {
    if (end <= start) return;
    const gap = src.slice(start, end);
    let buf = "";
    const flush = () => {
      if (!buf) return;
      out.push({ i: synthIdx--, s: buf });
      buf = "";
    };
    for (const ch of gap) {
      if (PUNCT_CHAR.test(ch)) {
        buf += ch;
      } else {
        flush();
      }
    }
    flush();
  };
  for (const tok of tokens) {
    const surface = String(tok?.s || "");
    if (!surface) {
      out.push(tok);
      continue;
    }
    const idx = src.indexOf(surface, cursor);
    if (idx < 0) {
      out.push(tok);
      continue;
    }
    emitGap(cursor, idx);
    out.push(tok);
    cursor = idx + surface.length;
  }
  emitGap(cursor, src.length);
  return out;
}

/**
 * Apply ``balanceQuotes`` to a parsed sentence object (text + tokens +
 * blankText). When a synthetic opening / closing quote is prepended or
 * appended, an extra token is inserted at the matching end so the
 * renderer (which walks ``tokens``) shows the balanced sentence too.
 */
function balanceSentenceQuotes(sent) {
  const text = normalizeStorySentenceText(sent?.text);
  if (!text) return sent;
  const balanced = balanceQuotes(text);
  const tokens = stripTrailingCommaTokens((sent.tokens || []).slice());
  if (balanced === text) {
    const blankText = sent.blankText ? balanceQuotes(String(sent.blankText)) : sent.blankText;
    return { ...sent, text, tokens, blankText };
  }
  const prepended = balanced.length > text.length && balanced.startsWith("\u201E") && !text.startsWith("\u201E");
  const appended = balanced.length > text.length && balanced.endsWith("\u201C") && !text.endsWith("\u201C");
  // Synthetic indexes (-1 / 1e6) cannot collide with real token ``i``
  // values from the tokenizer (which are byte offsets ≥ 0).
  if (prepended) tokens.unshift({ i: -1, s: "\u201E" });
  if (appended) tokens.push({ i: 1e6, s: "\u201C" });
  const blankText = balanceQuotes(String(sent.blankText || ""));
  return { ...sent, text: balanced, blankText, tokens };
}

function verbBracketEndIndex(tokens) {
  const verbs = (tokens || []).filter((t) => String(t?.g || "").startsWith("V"));
  if (verbs.length >= 2) {
    return Number(verbs[verbs.length - 1].i);
  }
  // Authored / naive token rows without POS: last word after the first verb-like
  // target is treated as the bracket infinitive (supports *schwimmen gehen*).
  const wordTokens = (tokens || []).filter((t) => /\w/u.test(String(t?.s || "").replace(/[.,!?]/g, "")));
  if (wordTokens.length < 2) return null;
  return Number(wordTokens[wordTokens.length - 1].i);
}

function addModalBracketTargetIfNeeded(sent) {
  const targets = [...(sent?.targetIndexes || [])].map((i) => Number(i));
  if (targets.length >= 2) return sent;
  if (targets.length !== 1) return sent;
  const tokens = sent?.tokens || [];
  const endVerb = verbBracketEndIndex(tokens);
  if (endVerb == null || targets.includes(endVerb)) return sent;
  const modalIdx = targets[0];
  if (endVerb <= modalIdx) return sent;
  return { ...sent, targetIndexes: [...targets, endVerb] };
}

function describeStoryPattern(pattern) {
  if (!pattern || typeof pattern !== "object") return "";
  const bits = [];
  if (pattern.lemma) bits.push(`lemma=${Array.isArray(pattern.lemma) ? pattern.lemma.join("|") : pattern.lemma}`);
  if (Array.isArray(pattern.morph_all_of) && pattern.morph_all_of.length) {
    bits.push(`morph∋${pattern.morph_all_of.join("+")}`);
  }
  if (Array.isArray(pattern.morph_any_of) && pattern.morph_any_of.length) {
    bits.push(`morph∈{${pattern.morph_any_of.join("|")}}`);
  }
  if (pattern.g_prefix) bits.push(`g~${pattern.g_prefix}`);
  return bits.join(" ");
}

function grammarDebugLabels(uiLang = "de") {
  const debug = getGrammarUnitCommonStrings(uiLang).debug || {};
  return {
    fromText: String(debug.fromText || ""),
    authored: String(debug.authored || ""),
    noTextMatch: String(debug.noTextMatch || ""),
    noTextMatchPattern: String(debug.noTextMatchPattern || ""),
  };
}

/** Active debug label set while hydrating grammar modules for a UI language. */
let _activeGrammarDbg = grammarDebugLabels("de");

function _noTextMatchLabel(patternDesc) {
  return _activeGrammarDbg.noTextMatchPattern.replace("{pattern}", patternDesc || "");
}

/**
 * Pool-style story matching: collect every sentence from the linked story
 * that satisfies a single block-level ``pattern`` (e.g. "contains any
 * imperative form"), then distribute those hits across the items in this
 * mark_in_text block. When the pool runs out the authored fallback for
 * that slot is kept verbatim. Returns a flat array, one entry per item.
 *
 * The function is intentionally permissive on the input shape: each
 * incoming item is expected to carry exactly one authored sentence in
 * ``markInTextSentences[0]`` (helper convention). The returned entries
 * have the same shape, ready to be wrapped back into ``mark_in_text``
 * items by ``hydrateMarkInTextBlockFromPool``.
 */
function collectPoolMatches(sentences, pattern) {
  const out = [];
  if (!Array.isArray(sentences) || !pattern) return out;
  for (const sent of sentences) {
    if (!sentenceLengthOk(sent, pattern)) continue;
    const tokens = sent?.tokens || [];
    const targets = [];
    for (const tok of tokens) {
      if (tokenMatchesPattern(tok, pattern)) targets.push(Number(tok.i));
    }
    if (!targets.length) continue;
    out.push({ sentence: sent, targets });
  }
  return out;
}

function hydrateMarkInTextBlockFromPool(items, src, poolPattern, crossBlockUsedRefs) {
  const pool = collectPoolMatches(src?.sentences || [], poolPattern);
  const patternDesc = describeStoryPattern(poolPattern);
  const sharedRefs = crossBlockUsedRefs instanceof Set ? crossBlockUsedRefs : null;
  // Sort the pool to **prefer sentences not yet consumed** by an earlier
  // exercise in the same unit (Wörter finden → select_one carry-over).
  // Within each preference group keep the original story order so the
  // renderer's progression mirrors the linear text.
  const ordered = sharedRefs
    ? [
        ...pool.filter((p) => !p.sentence?.ref || !sharedRefs.has(p.sentence.ref)),
        ...pool.filter((p) => p.sentence?.ref && sharedRefs.has(p.sentence.ref)),
      ]
    : pool;
  let cursor = 0;
  return (items || []).map((item) => {
    const sentences = item.markInTextSentences || [];
    const authored = sentences[0] || {};
    if (cursor < ordered.length) {
      const hit = ordered[cursor++];
      const sentText = normalizeStorySentenceText(hit.sentence.text || "");
      const enriched = enrichTokensWithPunctuation(sentText, hit.sentence.tokens || []);
      const balanced = balanceSentenceQuotes({
        text: sentText,
        tokens: enriched,
        blankText: rebuildBlankText(sentText, enriched, hit.targets),
      });
      if (sharedRefs && hit.sentence.ref) sharedRefs.add(hit.sentence.ref);
      const swapped = {
        ref: hit.sentence.ref,
        text: balanced.text,
        blankText: balanced.blankText,
        tokens: balanced.tokens,
        targetIndexes: hit.targets,
        sourceLabel: `${_activeGrammarDbg.fromText} (${hit.sentence.ref || "?"})`,
      };
      return { ...item, markInTextSentences: [swapped] };
    }
    return {
      ...item,
      markInTextSentences: [
        {
          ...authored,
          storyPattern: undefined,
          sourceLabel: _noTextMatchLabel(patternDesc),
        },
      ],
    };
  });
}

/**
 * Generic "mark in the text" fill for blocks that have no authored
 * ``storyPoolPattern``. Uses the evaluator-provided sentence pool directly:
 * every sentence already carries the phenomenon's ``targetIndexes`` (from the
 * unit evaluator), so we just slot story sentences into the authored items and
 * highlight those target tokens. Falls back to the authored sentence when the
 * pool runs out.
 */
function hydrateMarkInTextBlockGeneric(items, src, crossBlockUsedRefs) {
  const pool = (src?.sentences || []).filter(
    (s) =>
      Array.isArray(s.targetIndexes) &&
      s.targetIndexes.length &&
      sentenceLengthOk(s, { max_tokens: 30 }),
  );
  const sharedRefs = crossBlockUsedRefs instanceof Set ? crossBlockUsedRefs : null;
  const ordered = sharedRefs
    ? [
        ...pool.filter((p) => !p.ref || !sharedRefs.has(p.ref)),
        ...pool.filter((p) => p.ref && sharedRefs.has(p.ref)),
      ]
    : pool;
  let cursor = 0;
  return (items || []).map((item) => {
    const sentences = item.markInTextSentences || [];
    const authored = sentences[0] || {};
    if (cursor < ordered.length) {
      const sent = ordered[cursor++];
      const sentText = normalizeStorySentenceText(sent.text || "");
      const enriched = enrichTokensWithPunctuation(sentText, sent.tokens || []);
      const targets = [...(sent.targetIndexes || [])].map((i) => Number(i));
      const balanced = balanceSentenceQuotes({
        text: sentText,
        tokens: enriched,
        blankText: rebuildBlankText(sentText, enriched, targets),
      });
      if (sharedRefs && sent.ref) sharedRefs.add(sent.ref);
      return {
        ...item,
        markInTextSentences: [
          {
            ref: sent.ref,
            text: balanced.text,
            blankText: balanced.blankText,
            tokens: balanced.tokens,
            targetIndexes: targets,
            sourceLabel: `${_activeGrammarDbg.fromText} (${sent.ref || "?"})`,
          },
        ],
      };
    }
    return {
      ...item,
      markInTextSentences: [{ ...authored, storyPattern: undefined, sourceLabel: _activeGrammarDbg.authored }],
    };
  });
}

function hydrateMarkInTextWithStoryPatterns(item, src, crossBlockUsedRefs) {
  const sentences = item.markInTextSentences || [];
  if (!sentences.some((s) => s.storyPattern)) {
    // Pure authored items (no patterns at all) — tag once for debug output.
    return sentences.map((authored) => ({ ...authored, sourceLabel: _activeGrammarDbg.authored }));
  }
  const used = new Set();
  const sharedRefs = crossBlockUsedRefs instanceof Set ? crossBlockUsedRefs : null;
  const rebuilt = sentences.map((authored) => {
    const pattern = authored.storyPattern;
    const patternDesc = describeStoryPattern(pattern);
    if (!pattern) {
      return { ...authored, sourceLabel: _activeGrammarDbg.authored };
    }
    if (!src?.sentences?.length) {
      return { ...authored, sourceLabel: _noTextMatchLabel(patternDesc) };
    }
    const preferVerbBracket = (authored.targetIndexes || []).length >= 2;
    const match = findStoryMatch(src.sentences, pattern, used, {
      preferVerbBracket,
      sharedRefs,
    });
    if (!match) {
      return { ...authored, sourceLabel: _noTextMatchLabel(patternDesc) };
    }
    used.add(match.sentence.ref);
    if (sharedRefs && match.sentence.ref) sharedRefs.add(match.sentence.ref);
    const tokens = match.sentence.tokens || [];
    const targets = [];
    for (const tok of tokens) {
      if (tokenMatchesPattern(tok, pattern)) targets.push(Number(tok.i));
    }
    if (!targets.length) {
      return { ...authored, sourceLabel: _noTextMatchLabel(patternDesc) };
    }
    // Verb bracket (e.g. modal + infinitive): keep the sentence-final verb too.
    if ((authored.targetIndexes || []).length >= 2) {
      const endVerb = verbBracketEndIndex(tokens);
      if (endVerb != null && !targets.includes(endVerb)) targets.push(endVerb);
    }
    const sentText = normalizeStorySentenceText(match.sentence.text || "");
    const enriched = enrichTokensWithPunctuation(sentText, tokens);
    const balanced = balanceSentenceQuotes({
      text: sentText,
      tokens: enriched,
      blankText: rebuildBlankText(sentText, enriched, targets),
    });
    return {
      ref: match.sentence.ref,
      text: balanced.text,
      blankText: balanced.blankText,
      tokens: balanced.tokens,
      targetIndexes: targets,
      sourceLabel: `${_activeGrammarDbg.fromText} (${match.sentence.ref || "?"})`,
    };
  });
  return rebuilt.map(({ storyPattern: _drop, ...rest }) => rest);
}

function markInTextItemUsesStorySentence(item) {
  const sent = item?.markInTextSentences?.[0];
  const label = String(sent?.sourceLabel || "");
  return label.startsWith(_activeGrammarDbg.fromText);
}

/** After story hydration: authored / no-match fallbacks first, text hits last. */
function reorderMarkInTextItemsAuthoredFirst(items) {
  if (!Array.isArray(items) || items.length < 2) return items;
  if (!items.every((i) => Array.isArray(i.markInTextSentences) && i.markInTextSentences.length)) {
    return items;
  }
  const authored = [];
  const fromStory = [];
  for (const item of items) {
    if (markInTextItemUsesStorySentence(item)) fromStory.push(item);
    else authored.push(item);
  }
  if (!authored.length || !fromStory.length) return items;
  return [...authored, ...fromStory];
}

function selectOneItemUsesStorySentence(item) {
  const label = String(item?.sourceLabel || "");
  return label.startsWith(_activeGrammarDbg.fromText);
}

/** After story hydration: authored / no-match fallbacks first, story hits last. */
function reorderSelectOneItemsAuthoredFirst(items) {
  if (!Array.isArray(items) || items.length < 2) return items;
  if (!items.every((i) => Array.isArray(i.elements) && i.elements.length && i.solutions?.length)) {
    return items;
  }
  const fromStory = [];
  const fallbacks = [];
  for (const item of items) {
    if (selectOneItemUsesStorySentence(item)) fromStory.push(item);
    else fallbacks.push(item);
  }
  if (!fromStory.length || !fallbacks.length) return items;
  return [...fallbacks, ...fromStory];
}

/* ------------------------------------------------------------------
 * Axis-aware select_one hydration
 *
 * When a select_one block declares ``primaryAxis`` ("slot" for form
 * drills, "lemma" for meaning drills) plus a ``paradigm`` and
 * ``lemmaPool``, the hydrator picks story sentences that maximise
 * coverage along the primary axis (every slot or every lemma at least
 * once) and diversity along the secondary axis (avoid reusing the same
 * lemma / slot across questions). Choices are rebuilt from the
 * paradigm so the question still tests the targeted axis.
 *
 * Recipes that do not declare an axis fall back to the legacy per-item
 * ``storyPattern`` matcher (kept below for compatibility).
 * ------------------------------------------------------------------ */

const _SLOT_PIECES = ["1.sg", "2.sg", "3.sg", "1.pl", "2.pl", "3.pl"];

function slotFromMorph(morph) {
  const m = String(morph || "");
  for (const slot of _SLOT_PIECES) {
    if (morphContainsPiece(m, slot)) return slot;
  }
  return null;
}

function lemmaFromParadigmSurface(paradigm, surface) {
  if (!paradigm || !surface) return null;
  const s = String(surface).toLowerCase();
  for (const lemma of Object.keys(paradigm)) {
    const slots = paradigm[lemma] || {};
    for (const slot of Object.keys(slots)) {
      if (String(slots[slot] || "").toLowerCase() === s) return lemma;
    }
  }
  return null;
}

/** Pick 4 distinct surface forms covering different slots of one lemma. */
function buildFormChoices(paradigm, lemma, answerSlot) {
  const slots = paradigm?.[lemma];
  if (!slots) return null;
  const answerForm = slots[answerSlot];
  if (!answerForm) return null;
  const used = new Set([String(answerForm).toLowerCase()]);
  const out = [answerForm];
  // Prefer slots whose surface form is distinct from the answer (modals
  // collapse 1.sg=3.sg and 1.pl=3.pl, so we walk in a pedagogically
  // useful order).
  const order = ["2.sg", "2.pl", "1.pl", "1.sg", "3.sg", "3.pl"];
  for (const slot of order) {
    if (out.length >= 4) break;
    if (slot === answerSlot) continue;
    const form = slots[slot];
    if (!form) continue;
    const key = String(form).toLowerCase();
    if (used.has(key)) continue;
    used.add(key);
    out.push(form);
  }
  // Fallback: top up with any remaining slot forms even if they collide
  // (rare; keeps the choice list at 4).
  for (const slot of _SLOT_PIECES) {
    if (out.length >= 4) break;
    const form = slots[slot];
    if (!form) continue;
    const key = String(form).toLowerCase();
    if (used.has(key)) continue;
    used.add(key);
    out.push(form);
  }
  return { choices: out, answerIndex: 0 };
}

/** Pick 4 distinct lemmas at the same slot (one is the answer lemma). */
function buildMeaningChoices(paradigm, lemmaPool, answerLemma, slot, preferredDistractors) {
  const slots = paradigm?.[answerLemma];
  if (!slots || !slots[slot]) return null;
  const used = new Set([String(answerLemma)]);
  const out = [slots[slot]];
  const lemmas = Array.isArray(preferredDistractors) && preferredDistractors.length
    ? preferredDistractors
    : (Array.isArray(lemmaPool) ? lemmaPool : Object.keys(paradigm));
  for (const lem of lemmas) {
    if (out.length >= 4) break;
    if (used.has(lem)) continue;
    const form = paradigm?.[lem]?.[slot];
    if (!form) continue;
    used.add(lem);
    out.push(form);
  }
  // Distinct surface forms only.
  const seenSurface = new Set();
  const dedup = [];
  for (const c of out) {
    const k = String(c).toLowerCase();
    if (seenSurface.has(k)) continue;
    seenSurface.add(k);
    dedup.push(c);
  }
  if (dedup.length < 2) return null;
  return { choices: dedup, answerIndex: 0 };
}

/** Index modal hits in the story by ``slot`` and by ``lemma``. */
function buildStoryModalIndex(src, paradigm) {
  const bySlot = {};
  const byLemma = {};
  if (!src?.sentences?.length || !paradigm) return { bySlot, byLemma };
  for (const sent of src.sentences) {
    const tokens = sent.tokens || [];
    if (!tokens.length) continue;
    if (!sentenceLengthOk(sent, { max_tokens: 25 })) continue;
    for (const tok of tokens) {
      const lemma = String(tok.l || "").toLowerCase();
      if (!paradigm[lemma]) continue;
      const slot = slotFromMorph(tok.m);
      if (!slot) continue;
      // Only present-tense indicative finite forms qualify.
      if (!morphContainsPiece(tok.m, "ind.pres.fin")) continue;
      const hit = { sentence: sent, token: tok, slot, lemma };
      (bySlot[slot] = bySlot[slot] || []).push(hit);
      (byLemma[lemma] = byLemma[lemma] || []).push(hit);
    }
  }
  return { bySlot, byLemma };
}

function pickBestHit(hits, usedRefs, prefer) {
  if (!hits || !hits.length) return null;
  const usedLemmas = (prefer && prefer.usedLemmas) || new Set();
  const usedSlots = (prefer && prefer.usedSlots) || new Set();
  // Sentences already consumed by an earlier select_one block in the same
  // unit hydration. We treat reuse as a *soft* penalty (last resort) so a
  // second block with the same target axis still gets a story sentence
  // when no fresh one exists. See recipes-design.md §4g.
  const crossBlockUsedRefs = (prefer && prefer.crossBlockUsedRefs) || new Set();
  const ranked = [];
  for (const h of hits) {
    if (usedRefs.has(h.sentence.ref)) continue;
    const crossBlockPenalty = crossBlockUsedRefs.has(h.sentence.ref) ? 1 : 0;
    const lemmaPenalty = usedLemmas.has(h.lemma) ? 1 : 0;
    const slotPenalty = usedSlots.has(h.slot) ? 1 : 0;
    const tokenCount = (h.sentence.tokens || []).filter((t) => String(t?.s || "").trim()).length;
    ranked.push({ hit: h, crossBlockPenalty, lemmaPenalty, slotPenalty, tokenCount });
  }
  if (!ranked.length) return null;
  ranked.sort((a, b) => {
    if (a.crossBlockPenalty !== b.crossBlockPenalty) return a.crossBlockPenalty - b.crossBlockPenalty;
    if (a.lemmaPenalty !== b.lemmaPenalty) return a.lemmaPenalty - b.lemmaPenalty;
    if (a.slotPenalty !== b.slotPenalty) return a.slotPenalty - b.slotPenalty;
    return a.tokenCount - b.tokenCount;
  });
  return ranked[0].hit;
}

function makeSelectOneStoryItem(item, hit, choices, answerIndex) {
  const text = String(hit.sentence.text || "");
  const promptText = rebuildBlankText(text, hit.sentence.tokens || [], [Number(hit.token.i)]);
  return {
    ...item,
    text: [promptText],
    elements: choices,
    solutions: [String(answerIndex)],
    sourceLabel: `${_activeGrammarDbg.fromText} (${hit.sentence.ref || "?"})`,
  };
}

function hydrateSelectOneAxisAware(items, src, blockMeta, crossBlockUsedRefs) {
  const paradigm = blockMeta?.paradigm;
  const axis = blockMeta?.primaryAxis;
  const lemmaPool = Array.isArray(blockMeta?.lemmaPool) ? blockMeta.lemmaPool : null;
  if (!paradigm || (axis !== "slot" && axis !== "lemma")) return null;
  const index = buildStoryModalIndex(src, paradigm);
  const usedRefs = new Set();
  const usedLemmas = new Set();
  const usedSlots = new Set();
  const sharedRefs = crossBlockUsedRefs instanceof Set ? crossBlockUsedRefs : null;
  const out = [];
  for (const item of items) {
    let storyItem = null;
    const prefer = { usedLemmas, usedSlots, crossBlockUsedRefs: sharedRefs };
    if (axis === "slot") {
      const targetSlot = String(item.targetSlot || "").trim();
      if (targetSlot) {
        const hit = pickBestHit(index.bySlot[targetSlot], usedRefs, prefer);
        if (hit) {
          const built = buildFormChoices(paradigm, hit.lemma, hit.slot);
          if (built) {
            usedRefs.add(hit.sentence.ref);
            usedLemmas.add(hit.lemma);
            usedSlots.add(hit.slot);
            if (sharedRefs && hit.sentence.ref) sharedRefs.add(hit.sentence.ref);
            storyItem = makeSelectOneStoryItem(item, hit, built.choices, built.answerIndex);
          }
        }
      }
    } else if (axis === "lemma") {
      const targetLemma = String(item.targetLemma || "").trim().toLowerCase();
      if (targetLemma) {
        const hit = pickBestHit(index.byLemma[targetLemma], usedRefs, prefer);
        if (hit) {
          const built = buildMeaningChoices(paradigm, lemmaPool, hit.lemma, hit.slot);
          if (built) {
            usedRefs.add(hit.sentence.ref);
            usedLemmas.add(hit.lemma);
            usedSlots.add(hit.slot);
            if (sharedRefs && hit.sentence.ref) sharedRefs.add(hit.sentence.ref);
            storyItem = makeSelectOneStoryItem(item, hit, built.choices, built.answerIndex);
          }
        }
      }
    }
    out.push(storyItem || item);
  }
  return out;
}

function describeAxisFallback(axis, item) {
  if (axis === "slot" && item.targetSlot) {
    return _noTextMatchLabel(`slot=${item.targetSlot}`);
  }
  if (axis === "lemma" && item.targetLemma) {
    return _noTextMatchLabel(`lemma=${item.targetLemma}`);
  }
  if (item.storyPattern) {
    return _noTextMatchLabel(describeStoryPattern(item.storyPattern));
  }
  // Every select_one item is, by design, *eligible* for story
  // substitution (see ``recipes-design.md`` §4). When the recipe didn't
  // ship a story_pattern / recognition pool for this slot, we still
  // mark it as "no text match" rather than "Autor:innen-Beispiel" — the
  // latter label is reserved for exercise types (``order_horizontal``,
  // ``select_multiple_blank``) that are intentionally always authored.
  return _activeGrammarDbg.noTextMatch;
}

/** Legacy per-item story_pattern matcher (kept for recipes without a primary axis). */
function hydrateSelectOneLegacyPattern(item, src, usedRefs, crossBlockUsedRefs) {
  if (!item.storyPattern || !src?.sentences?.length) return null;
  const elements = item.elements || [];
  if (!elements.length) return null;
  const lowered = elements.map((e) => String(e || "").toLowerCase());
  // Two passes: prefer sentences not yet used by an earlier select_one
  // block in the same unit; only fall back to reused ones when no fresh
  // hit exists. Within-block dedup (``usedRefs``) stays a hard skip.
  const sharedRefs = crossBlockUsedRefs instanceof Set ? crossBlockUsedRefs : null;
  const passes = sharedRefs ? [true, false] : [false];
  for (const skipShared of passes) {
    for (const sent of src.sentences) {
      if (usedRefs && sent?.ref && usedRefs.has(sent.ref)) continue;
      if (skipShared && sharedRefs && sent?.ref && sharedRefs.has(sent.ref)) continue;
      if (!sentenceLengthOk(sent, item.storyPattern)) continue;
      const tokens = sent.tokens || [];
      for (const tok of tokens) {
        if (!tokenMatchesPattern(tok, item.storyPattern)) continue;
        const surface = String(tok.s || "").trim();
        const idx = lowered.indexOf(surface.toLowerCase());
        if (idx < 0) continue;
        const text = String(sent.text || "");
        const promptText = rebuildBlankText(text, tokens, [Number(tok.i)]);
        if (usedRefs && sent.ref) usedRefs.add(sent.ref);
        if (sharedRefs && sent.ref) sharedRefs.add(sent.ref);
        return {
          ...item,
          text: [promptText],
          elements,
          solutions: [String(idx)],
          sourceLabel: `${_activeGrammarDbg.fromText} (${sent.ref || "?"})`,
        };
      }
    }
  }
  return null;
}

/**
 * Recognition-pool matching for `select_one`.
 *
 * Used by **recognition** exercises (binary choice over a whole
 * sentence, e.g. *Imperativ?* / *Kein Imperativ?*). The block declares
 * one sentence-level pattern per correct ``answer_index`` in
 * `recognitionPools`. The hydrator collects matching sentences for each
 * pool from the linked story and, for every item whose first accepted
 * answer index equals `k`, pops one sentence off `pool[k]` to use as
 * the prompt. The choice set + answer indexes remain unchanged.
 *
 * Returns `null` when no item is improved (caller keeps the originals).
 */
function hydrateSelectOneRecognitionPool(items, src, recognitionPools, crossBlockUsedRefs) {
  if (!recognitionPools || typeof recognitionPools !== "object") return null;
  if (!Array.isArray(items) || !items.length) return null;
  if (!src?.sentences?.length) return null;
  const sharedRefs = crossBlockUsedRefs instanceof Set ? crossBlockUsedRefs : null;
  const pools = {};
  for (const key of Object.keys(recognitionPools)) {
    const pattern = recognitionPools[key];
    if (!pattern || typeof pattern !== "object") continue;
    // Collect every qualifying sentence, then partition so fresh refs
    // (not yet consumed by an earlier block) come first. Reused refs
    // stay in the pool as a last-resort tail so the exercise never
    // silently falls back to authored when the story offers *any* hit.
    const fresh = [];
    const reused = [];
    for (const sent of src.sentences) {
      if (!sentenceMatchesPattern(sent, pattern)) continue;
      if (sharedRefs && sent?.ref && sharedRefs.has(sent.ref)) reused.push(sent);
      else fresh.push(sent);
    }
    pools[key] = { pattern, hits: [...fresh, ...reused], cursor: 0 };
  }
  const usedRefs = new Set();
  const out = items.map((item) => {
    const sol = String((item.solutions || [""])[0] || "");
    const firstAcceptedIdx = sol.length ? sol[0] : null;
    if (firstAcceptedIdx === null) return item;
    const pool = pools[firstAcceptedIdx];
    if (!pool) return item;
    // Take the next unused sentence from this pool.
    while (pool.cursor < pool.hits.length) {
      const sent = pool.hits[pool.cursor++];
      if (!sent || (sent.ref && usedRefs.has(sent.ref))) continue;
      if (sent.ref) {
        usedRefs.add(sent.ref);
        if (sharedRefs) sharedRefs.add(sent.ref);
      }
      const balanced = balanceQuotes(
        normalizeStorySentenceText(stripTrailingClosingQuotes(sent.text || ""))
      );
      return {
        ...item,
        text: [balanced],
        sourceLabel: `${_activeGrammarDbg.fromText} (${sent.ref || "?"})`,
      };
    }
    // Pool exhausted — keep the authored prompt but flag the attempt.
    return {
      ...item,
      sourceLabel: _noTextMatchLabel(describeStoryPattern(pool.pattern)),
    };
  });
  // Return ``null`` if literally nothing changed, so the caller can
  // continue with axis-aware / legacy paths unchanged.
  return out.some((o, i) => o !== items[i]) ? out : null;
}

/**
 * Generic story-sourcing for ``select_one`` blocks that carry no axis
 * paradigm / recognition pool / legacy pattern. Keeps each authored item's
 * curated answer choices, but replaces the prompt with a real story sentence
 * whose evaluator *target* token surface matches one of those choices — the
 * matched token is blanked and the solution is set to that choice's index.
 *
 * This reuses the unit's curated distractors (no synthetic choice generation)
 * while guaranteeing the correct answer is the form the author actually used
 * in the selected text. Returns ``null`` when no item could be sourced.
 */
function hydrateSelectOneFromStoryChoices(items, src, crossBlockUsedRefs) {
  if (!Array.isArray(items) || !items.length) return null;
  if (!src?.sentences?.length) return null;
  const sharedRefs = crossBlockUsedRefs instanceof Set ? crossBlockUsedRefs : null;
  const candidates = src.sentences.filter(
    (s) =>
      Array.isArray(s.targetIndexes) &&
      s.targetIndexes.length &&
      sentenceLengthOk(s, { max_tokens: 28 }),
  );
  const pool = sharedRefs
    ? [
        ...candidates.filter((s) => !s.ref || !sharedRefs.has(s.ref)),
        ...candidates.filter((s) => s.ref && sharedRefs.has(s.ref)),
      ]
    : candidates;
  const usedRefs = new Set();
  let changed = false;
  const out = items.map((item) => {
    const choices = (item.elements || []).map((e) => String(e));
    if (choices.length < 2) return item;
    const lowToIdx = new Map();
    choices.forEach((c, i) => {
      const k = c.trim().toLowerCase();
      if (k && !lowToIdx.has(k)) lowToIdx.set(k, i);
    });
    for (const sent of pool) {
      if (sent.ref && usedRefs.has(sent.ref)) continue;
      const tokens = sent.tokens || [];
      const targets = (sent.targetIndexes || []).map(Number);
      let matchIdx = -1;
      let matchTok = null;
      for (const ti of targets) {
        const tok = tokens.find((t) => Number(t.i) === ti);
        if (!tok) continue;
        const surf = String(tok.s || "").trim().toLowerCase();
        if (lowToIdx.has(surf)) {
          matchIdx = lowToIdx.get(surf);
          matchTok = tok;
          break;
        }
      }
      if (matchIdx < 0 || !matchTok) continue;
      const sentText = normalizeStorySentenceText(sent.text || "");
      const enriched = enrichTokensWithPunctuation(sentText, tokens);
      const promptText = rebuildBlankText(sentText, enriched, [Number(matchTok.i)]);
      if (!promptText || promptText.indexOf("*") < 0) continue;
      if (sent.ref) {
        usedRefs.add(sent.ref);
        if (sharedRefs) sharedRefs.add(sent.ref);
      }
      changed = true;
      return {
        ...item,
        text: [promptText],
        elements: item.elements,
        solutions: [String(matchIdx)],
        acceptedSolutions: undefined,
        sourceLabel: `${_activeGrammarDbg.fromText} (${sent.ref || "?"})`,
      };
    }
    return item;
  });
  return changed ? out : null;
}

function hydrateSelectOneExerciseItems(items, src, unitInstructions, blockMeta, crossBlockUsedRefs) {
  const validItems = (items || []).filter(
    (i) => i?.elements?.length && i?.solutions?.length
  );
  let perItemHydrated = null;
  if (blockMeta?.primaryAxis && blockMeta?.paradigm) {
    perItemHydrated = hydrateSelectOneAxisAware(validItems, src, blockMeta, crossBlockUsedRefs);
  }
  let recognitionHydrated = null;
  if (blockMeta?.recognitionPools) {
    recognitionHydrated = hydrateSelectOneRecognitionPool(
      validItems,
      src,
      blockMeta.recognitionPools,
      crossBlockUsedRefs,
    );
  }
  const usedRefs = new Set();
  const hydrated = [];
  // Track which items the paradigm / recognition / legacy paths already
  // sourced from the text, so the generic choice-matching fallback only runs
  // for the still-authored ones (and keeps refs distinct across both).
  const preliminary = [];
  for (let i = 0; i < validItems.length; i++) {
    const original = validItems[i];
    let out = perItemHydrated ? perItemHydrated[i] : original;
    if (out === original && recognitionHydrated) {
      out = recognitionHydrated[i] || original;
    }
    if (out === original && original.storyPattern) {
      const legacy = hydrateSelectOneLegacyPattern(original, src, usedRefs, crossBlockUsedRefs);
      if (legacy) out = legacy;
    }
    preliminary.push(out);
  }
  // Generic fallback: for items still on the authored prompt, swap in a story
  // sentence whose target form matches one of the curated choices.
  const stillAuthored = preliminary
    .map((out, i) => (out === validItems[i] ? validItems[i] : null))
    .filter(Boolean);
  const choiceHydrated = stillAuthored.length
    ? hydrateSelectOneFromStoryChoices(stillAuthored, src, crossBlockUsedRefs)
    : null;
  let choiceCursor = 0;
  for (let i = 0; i < validItems.length; i++) {
    const original = validItems[i];
    let out = preliminary[i];
    if (out === original && choiceHydrated) {
      const sourced = choiceHydrated[choiceCursor++];
      if (sourced && sourced !== original) out = sourced;
    } else if (out === original) {
      choiceCursor++;
    }
    let sourceLabel = out.sourceLabel;
    if (!sourceLabel) {
      sourceLabel = describeAxisFallback(blockMeta?.primaryAxis, original);
    }
    hydrated.push({
      type: out.type || "select_one",
      text: out.text || [],
      elements: out.elements,
      solutions: out.solutions,
      acceptedSolutions: out.acceptedSolutions || undefined,
      hint: out.hint || undefined,
      instructions: out.instructions || unitInstructions,
      sourceLabel: GRAMMAR_USE_STORY_SENTENCES ? sourceLabel : undefined,
    });
  }
  return reorderSelectOneItemsAuthoredFirst(hydrated);
}

// Kept exported-name compatibility for older call-sites; not used directly anymore.
// eslint-disable-next-line no-unused-vars
function _legacySelectOneLemmaFromSurface(form, paradigm) {
  return lemmaFromParadigmSurface(paradigm, form);
}

/** @param {Record<string, unknown>} item */
function markInTextExerciseAuthoredOnly(item, unitInstructions) {
  const sentences = (item.markInTextSentences || []).map((/** @type {any} */ s) => {
    const authored = s || {};
    const { storyPattern: _sp, sourceLabel: _sl, ...rest } = authored;
    return rest;
  });
  return {
    type: "mark_in_text",
    text: item.text || sentences.map((/** @type {any} */ s) => s.text).filter(Boolean),
    markInTextSentences: sentences,
    instructions: item.instructions || unitInstructions,
  };
}

/** @param {Record<string, unknown>} item */
function selectOneExerciseAuthoredOnly(item, unitInstructions) {
  const { storyPattern: _sp, sourceLabel: _sl, ...rest } = item || {};
  return {
    type: "select_one",
    text: rest.text || [],
    elements: rest.elements,
    solutions: rest.solutions,
    acceptedSolutions: rest.acceptedSolutions || undefined,
    hint: rest.hint || undefined,
    instructions: rest.instructions || unitInstructions,
  };
}

function hydrateGrammarExerciseItem(item, src, unitInstructions, crossBlockUsedRefs) {
  if (!item) return null;
  if (item.markInTextSentences?.length) {
    if (!GRAMMAR_USE_STORY_SENTENCES) {
      return markInTextExerciseAuthoredOnly(item, unitInstructions);
    }
    const swapped = hydrateMarkInTextWithStoryPatterns(item, src, crossBlockUsedRefs);
    const sentences = swapped || item.markInTextSentences;
    return {
      type: "mark_in_text",
      text: item.text || sentences.map((s) => s.text).filter(Boolean),
      markInTextSentences: sentences,
      instructions: item.instructions || unitInstructions,
    };
  }
  if (
    !GRAMMAR_USE_STORY_SENTENCES &&
    item.type === "select_one" &&
    item.elements?.length &&
    item.solutions?.length
  ) {
    return selectOneExerciseAuthoredOnly(item, unitInstructions);
  }
  if (
    item.elements?.length &&
    item.solutions?.length &&
    item.type !== "select_one"
  ) {
    return {
      type: item.type,
      text: item.text || [],
      elements: item.elements,
      solutions: item.solutions,
      acceptedSolutions: item.acceptedSolutions || undefined,
      staticTokens: item.staticTokens || undefined,
      hint: item.hint || undefined,
      instructions: item.instructions || unitInstructions,
      sourceLabel: GRAMMAR_USE_STORY_SENTENCES ? item.sourceLabel || _activeGrammarDbg.authored : undefined,
    };
  }
  if (!GRAMMAR_USE_STORY_SENTENCES || !src?.sentences?.length) return null;
  const sentences = src.sentences;

  if (item.type === "mark_in_text") {
    return {
      type: "mark_in_text",
      text: sentences.map((s) => s.text).filter(Boolean),
      markInTextSentences: sentences,
      instructions: item.instructions || unitInstructions,
    };
  }

  if (item.type === "select_multiple_blank" && item.mode === "mark_targets_blank") {
    const maxLines = item.maxLines || 3;
    const maxBlanks = item.maxBlanks || 4;
    const lines = [];
    const solutionChars = [];
    const correctWords = [];
    let blankCount = 0;
    for (const sent of sentences.slice(0, maxLines)) {
      if (blankCount >= maxBlanks) break;
      if (sent.blankText) {
        lines.push(sent.blankText);
        const targets = sent.targetIndexes || [];
        for (const tid of targets) {
          const tok = (sent.tokens || []).find(t => t.i === tid);
          if (tok) {
            const s = String(tok.s || "");
            let idx = correctWords.indexOf(s);
            if (idx === -1) {
              correctWords.push(s);
              idx = correctWords.length - 1;
            }
            solutionChars.push(String(idx));
            blankCount++;
          }
        }
      } else {
        const targets = new Set(sent.targetIndexes || []);
        let line = "";
        for (const tok of sent.tokens || []) {
          const s = String(tok.s || "");
          if (!s.trim()) continue;
          if (targets.has(tok.i) && blankCount < maxBlanks) {
            let idx = correctWords.indexOf(s);
            if (idx === -1) {
              correctWords.push(s);
              idx = correctWords.length - 1;
            }
            solutionChars.push(String(idx));
            line += " * ";
            blankCount++;
          } else {
            line += " " + s;
          }
        }
        const trimmed = line.replace(/\s+/g, " ").trim();
        if (trimmed) lines.push(trimmed);
      }
    }
    if (!lines.length || !correctWords.length) return null;
    const distractors = distractorWords(sentences, correctWords);
    return {
      type: "select_multiple_blank",
      text: lines,
      elements: [...correctWords, ...distractors],
      solutions: [solutionChars.join("")],
      instructions: item.instructions || unitInstructions,
    };
  }

  if (item.type === "select_one") {
    const sent = sentences[0];
    const targets = new Set(sent?.targetIndexes || []);
    let targetWord = "";
    let targetTok = null;
    for (const tok of sent?.tokens || []) {
      if (targets.has(tok.i)) {
        targetWord = String(tok.s || "").trim();
        targetTok = tok;
        break;
      }
    }
    const distractors = getMorphologicalDistractors(
      targetWord,
      targetTok?.l,
      targetTok?.g,
      targetTok?.m
    );
    const elements = targetWord ? [targetWord, ...distractors] : distractors;
    if (!elements.length) return null;

    let promptText = sent?.text || item.prompt || "";
    if (targetWord) {
      const regex = new RegExp("\\b" + targetWord + "\\b", "i");
      if (regex.test(promptText)) {
        promptText = promptText.replace(regex, "*");
      } else {
        promptText = promptText.replace(targetWord, "*");
      }
    }

    return {
      type: "select_one",
      text: [promptText],
      elements,
      solutions: ["0"],
      instructions: item.instructions || unitInstructions,
    };
  }

  if (item.type === "order_vertical") {
    const elements =
      item.elements?.length > 0
        ? item.elements
        : ["Subjekt", "Verb (Position 2)", "Mittelfeld", "Ergänzung / Präfix"];
    return {
      type: "order_vertical",
      title: item.title || "",
      elements,
      solutions: [elements.map((_, i) => String(i)).join("")],
      instructions: item.instructions || unitInstructions,
    };
  }

  return null;
}

/** Story-shaped `modules` object for `text-content-client` from a content id. */
export function buildHydratedGrammarModules(unit, contentId, uiLang = "de") {
  _activeGrammarDbg = grammarDebugLabels(uiLang);
  const template = resolveGrammarUnitModules(unit, uiLang);
  const src = GRAMMAR_USE_STORY_SENTENCES ? unit.textSources?.[contentId] : null;
  const selectedExercises = unit.selectedUnitV3?.selectedUnit?.selectedExercises || [];
  const selectedByRecipeId = new Map();
  for (const selected of selectedExercises) {
    if (selected?.recipeId) selectedByRecipeId.set(String(selected.recipeId), selected);
  }
  const unitsOut = [];
  // Story sentences already shown earlier in the unit (Wörter finden →
  // select_one → another select_one …). Every block that swaps a story
  // sentence into a slot adds the ref here, and later blocks **prefer
  // sentences NOT in this set**. We don't reject reused refs outright —
  // when a story genuinely only has 3 imperative sentences and the unit
  // needs 6, recycling beats showing nothing — but new material is the
  // default. See ``recipes-design.md`` §4i.
  const crossBlockUsedRefs = new Set();

  for (const u of template.grammar?.units || []) {
    if (u.type === "rule") {
      unitsOut.push({
        type: "rule",
        title: u.title,
        explanationParagraphs: u.explanationParagraphs || normalizeExplanationParagraphs(u.explanation),
        examples: u.examples,
      });
      continue;
    }
    if (u.type === "anchors") {
      unitsOut.push({
        type: "anchors",
        title: u.title || "",
        items: Array.isArray(u.items) ? u.items : [],
      });
      continue;
    }
    if (u.type === "closure") continue;
    const rawItems = u.items || [];
    const selected = u.recipeId ? selectedByRecipeId.get(String(u.recipeId)) : null;
    if (selected && Array.isArray(selected.items) && selected.items.length) {
      unitsOut.push({
        type: "exercise",
        title: u.title,
        instructions: u.instructions,
        recipeId: u.recipeId || null,
        items: selected.items.map((item) => ({
          ...balanceSelectedV3ExerciseItem(item),
          instructions: item.instructions || u.instructions,
          sourceLabel: GRAMMAR_USE_STORY_SENTENCES ? item.sourceLabel || undefined : undefined,
        })),
      });
      continue;
    }
    const isSelectOneBlock =
      rawItems.length > 0 && rawItems.every((item) => item?.type === "select_one");
    const isMarkInTextBlock =
      rawItems.length > 0 && rawItems.every((item) => item?.type === "mark_in_text");
    const items = [];
    if (isSelectOneBlock) {
      if (GRAMMAR_USE_STORY_SENTENCES) {
        items.push(...hydrateSelectOneExerciseItems(rawItems, src, u.instructions, {
          primaryAxis: u.primaryAxis,
          paradigm: u.paradigm,
          lemmaPool: u.lemmaPool,
          recognitionPools: u.recognitionPools,
        }, crossBlockUsedRefs));
      } else {
        for (const item of rawItems) {
          items.push(selectOneExerciseAuthoredOnly(item, u.instructions));
        }
      }
    } else if (isMarkInTextBlock && u.storyPoolPattern && GRAMMAR_USE_STORY_SENTENCES) {
      // Pool-first matching: every story sentence containing the
      // phenomenon (`u.storyPoolPattern`) becomes a candidate, and the
      // hydrator fills the authored slots in order until the pool runs
      // out. This pulls "as much from the text as possible" without
      // forcing the recipe to declare a specific lemma+slot per anchor.
      // Passing ``crossBlockUsedRefs`` lets later blocks see which
      // sentences were already shown here (so a follow-up exercise can
      // prefer fresh material).
      const pooled = hydrateMarkInTextBlockFromPool(
        rawItems,
        src,
        u.storyPoolPattern,
        crossBlockUsedRefs,
      );
      for (const item of pooled) {
        items.push({
          type: "mark_in_text",
          text: (item.markInTextSentences || []).map((s) => s.text).filter(Boolean),
          markInTextSentences: item.markInTextSentences || [],
          instructions: item.instructions || u.instructions,
        });
      }
    } else if (isMarkInTextBlock && GRAMMAR_USE_STORY_SENTENCES && src?.sentences?.length) {
      // No authored ``storyPoolPattern`` on the block: fill directly from the
      // evaluator sentence pool, which already carries per-sentence
      // ``targetIndexes`` for the unit's phenomenon.
      const pooled = hydrateMarkInTextBlockGeneric(rawItems, src, crossBlockUsedRefs);
      for (const item of pooled) {
        items.push({
          type: "mark_in_text",
          text: (item.markInTextSentences || []).map((s) => s.text).filter(Boolean),
          markInTextSentences: item.markInTextSentences || [],
          instructions: item.instructions || u.instructions,
        });
      }
    } else if (isMarkInTextBlock) {
      for (const item of rawItems) {
        items.push(markInTextExerciseAuthoredOnly(item, u.instructions));
      }
    } else {
      for (const item of rawItems) {
        const hydrated = hydrateGrammarExerciseItem(item, src, u.instructions, crossBlockUsedRefs);
        if (hydrated) items.push(hydrated);
      }
    }
    if (items.length) {
      const orderedItems = reorderMarkInTextItemsAuthoredFirst(items);
      unitsOut.push({
        type: "exercise",
        title: u.title,
        instructions: u.instructions,
        recipeId: u.recipeId || null,
        items: orderedItems,
      });
    }
  }
  return { grammar: { units: unitsOut } };
}
